import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFieldArray, useForm, useWatch, type DeepPartial, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import posthog from 'posthog-js';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '../../components/ui/form';
import { cn } from '../../components/ui/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSkillsSearch } from '../../hooks/useSkills';
import { useOccupationSearch } from '../../hooks/useOccupations';
import {
  fetchCandidateOnboardingDraft,
  ONBOARDING_STORAGE_KEY,
  saveCandidateOnboarding,
  type CandidateOnboardingPayload,
} from '../../api/candidate';

const focusAreaSuggestions = ['Care', 'Operations', 'Product', 'Engineering', 'Design', 'Customer success', 'Returners'];
const skillSuggestions = ['Leadership', 'Data analysis', 'Patient care', 'Project management', 'Sales strategy', 'AI tools'];
const roleSuggestions = ['Care Lead', 'Service Designer', 'Programme Manager', 'Data Analyst', 'Operations Director'];

const teamMemberSchema = z.object({
  fullName: z.string().min(2, 'Team member name is required'),
  headline: z.string().min(2, 'Add a role headline'),
  cvSummary: z.string().min(20, 'Add a short CV summary'),
  cvSkills: z.array(z.string().min(2)).min(1, 'Add at least one CV skill'),
});

const formSchema = z
  .object({
    fullName: z.string().min(2, 'Please enter your name'),
    headline: z.string().min(10, 'Headline should be at least 10 characters'),
    story: z.string().min(30, 'Tell us a bit more about your story'),
    returner: z.boolean(),
    registrationMode: z.enum(['solo', 'team']).nullable(),
    teamValueProposition: z.string(),
    teamReliability: z.string(),
    teamAchievements: z.string(),
    teamHistory: z.string(),
    teamTenureYears: z.number().int().min(0).max(80),
    teamSize: z.number().int().min(1).max(50),
    teamMembers: z.array(teamMemberSchema),
    experienceLevel: z.enum(['early', 'mid', 'senior']),
    skills: z.array(z.string().min(2)).min(3, 'Add at least 3 skills'),
    focusAreas: z.array(z.string().min(2)).min(1, 'Pick at least one focus area'),
    desiredRoles: z.array(z.string().min(2)).min(1, 'Add at least one target role'),
    locationPreference: z.string().min(3, 'Where do you want to work?'),
    workStyle: z.enum(['remote', 'hybrid', 'onsite']),
    salaryRange: z.string().min(3, 'Please outline your salary expectation'),
    availabilityDate: z.string().min(3, 'Let us know when you can start'),
    jobAlerts: z.boolean(),
    alertFrequency: z.enum(['daily', 'weekly', 'off']),
    notificationChannels: z.array(z.enum(['email', 'sms', 'in-app'])),
  })
  .refine((data) => {
    if (!data.jobAlerts) {
      return true;
    }
    return data.notificationChannels.length > 0;
  }, {
    message: 'Select at least one channel',
    path: ['notificationChannels'],
  })
  .superRefine((data, ctx) => {
    if (data.registrationMode === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['registrationMode'],
        message: 'Choose how you want to register: solo or team.',
      });
      return;
    }
    if (data.registrationMode !== 'team') {
      return;
    }
    if (data.teamValueProposition.trim().length < 40) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamValueProposition'],
        message: 'Describe what your team offers in at least 40 characters.',
      });
    }
    if (data.teamReliability.trim().length < 30) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamReliability'],
        message: 'Share why your team is reliable (at least 30 characters).',
      });
    }
    if (data.teamAchievements.trim().length < 30) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamAchievements'],
        message: 'Add your team achievements and progress (at least 30 characters).',
      });
    }
    if (data.teamHistory.trim().length < 30) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamHistory'],
        message: 'Add short team history/background (at least 30 characters).',
      });
    }
    if (data.teamSize < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamSize'],
        message: 'Team registration requires at least 2 people.',
      });
    }
    if (data.teamMembers.length !== data.teamSize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamMembers'],
        message: 'Team member count must match team size.',
      });
    }
  });

type OnboardingFormValues = z.infer<typeof formSchema>;

const defaultValues: OnboardingFormValues = {
  fullName: '',
  headline: '',
  story: '',
  returner: true,
  registrationMode: null,
  teamValueProposition: '',
  teamReliability: '',
  teamAchievements: '',
  teamHistory: '',
  teamTenureYears: 0,
  teamSize: 1,
  teamMembers: [],
  experienceLevel: 'mid',
  skills: [],
  focusAreas: [],
  desiredRoles: [],
  locationPreference: '',
  workStyle: 'hybrid',
  salaryRange: '',
  availabilityDate: '',
  jobAlerts: true,
  alertFrequency: 'weekly',
  notificationChannels: ['email'],
};

const steps = [
  { id: 'basics', title: 'About you', description: 'Headline, story, and experience' },
  { id: 'skills', title: 'Skills & focus', description: 'Boost the AI matcher' },
  { id: 'preferences', title: 'Work preferences', description: 'Location, style, salary' },
  { id: 'alerts', title: 'Alerts & summary', description: 'Stay updated and confirm' },
] as const;

type StepId = (typeof steps)[number]['id'];

const stepFieldMap: Record<StepId, Array<keyof OnboardingFormValues>> = {
  basics: [
    'fullName',
    'headline',
    'story',
    'returner',
    'registrationMode',
    'teamValueProposition',
    'teamReliability',
    'teamAchievements',
    'teamHistory',
    'teamTenureYears',
    'teamSize',
    'teamMembers',
    'experienceLevel',
  ],
  skills: ['skills', 'focusAreas', 'desiredRoles'],
  preferences: ['locationPreference', 'workStyle', 'salaryRange', 'availabilityDate'],
  alerts: ['jobAlerts', 'alertFrequency', 'notificationChannels'],
};

const createTeamMember = (): OnboardingFormValues['teamMembers'][number] => ({
  fullName: '',
  headline: '',
  cvSummary: '',
  cvSkills: [],
});

export function CandidateOnboarding() {
  const [currentStep, setCurrentStep] = useState<StepId>('basics');
  const [initialized, setInitialized] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange',
  });
  const teamMembersFieldArray = useFieldArray({
    control: form.control,
    name: 'teamMembers',
  });
  const registrationMode = useWatch({ control: form.control, name: 'registrationMode' });
  const registerAsTeam = registrationMode === 'team';
  const leadName = useWatch({ control: form.control, name: 'fullName' });
  const leadHeadline = useWatch({ control: form.control, name: 'headline' });
  const leadStory = useWatch({ control: form.control, name: 'story' });
  const leadSkills = useWatch({ control: form.control, name: 'skills' });
  const watchedTeamSize = useWatch({ control: form.control, name: 'teamSize' });

  const onboardingDraftQuery = useQuery({
    queryKey: ['candidate-onboarding-draft'],
    queryFn: fetchCandidateOnboardingDraft,
    staleTime: 1000 * 60,
  });

  useEffect(() => {
    if (onboardingDraftQuery.isPending) return;
    if (onboardingDraftQuery.data) {
      const draftRegistrationMode = (
        onboardingDraftQuery.data as Partial<OnboardingFormValues>
      ).registrationMode;
      form.reset({
        ...defaultValues,
        ...onboardingDraftQuery.data,
        registrationMode:
          draftRegistrationMode ??
          (onboardingDraftQuery.data.registerAsTeam ? 'team' : 'solo'),
      });
    }
    setInitialized(true);
  }, [form, onboardingDraftQuery.data, onboardingDraftQuery.isPending]);

  useEffect(() => {
    if (!onboardingDraftQuery.isError) return;
    toast.error('Unable to load your saved onboarding details. Please continue and resubmit.');
  }, [onboardingDraftQuery.isError]);

  useEffect(() => {
    if (!initialized) return;

    if (!registerAsTeam) {
      if (form.getValues('teamMembers').length) {
        form.setValue('teamMembers', [], { shouldDirty: true, shouldValidate: true });
      }
      if (form.getValues('teamSize') !== 1) {
        form.setValue('teamSize', 1, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    const normalizedTeamSize = Math.min(50, Math.max(2, Math.trunc(Number(watchedTeamSize) || 2)));
    if (normalizedTeamSize !== watchedTeamSize) {
      form.setValue('teamSize', normalizedTeamSize, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const members = form.getValues('teamMembers');
    if (!members.length && normalizedTeamSize > 0) {
      teamMembersFieldArray.append(createTeamMember());
      return;
    }

    const [firstMember, ...otherMembers] = members;
    if (firstMember) {
      const syncedFirstMember = {
        ...firstMember,
        fullName: leadName || firstMember.fullName,
        headline: leadHeadline || firstMember.headline,
        cvSummary: leadStory || firstMember.cvSummary,
        cvSkills: leadSkills.length
          ? leadSkills
          : firstMember.cvSkills.length
          ? firstMember.cvSkills
          : ['Team leadership'],
      };
      if (
        syncedFirstMember.fullName !== firstMember.fullName ||
        syncedFirstMember.headline !== firstMember.headline ||
        syncedFirstMember.cvSummary !== firstMember.cvSummary ||
        syncedFirstMember.cvSkills.join(',') !== firstMember.cvSkills.join(',')
      ) {
        form.setValue('teamMembers', [syncedFirstMember, ...otherMembers], {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }
    }

    if (members.length < normalizedTeamSize) {
      const missing = normalizedTeamSize - members.length;
      for (let index = 0; index < missing; index += 1) {
        teamMembersFieldArray.append(createTeamMember());
      }
      return;
    }
    if (members.length > normalizedTeamSize) {
      teamMembersFieldArray.remove(
        Array.from({ length: members.length - normalizedTeamSize }, (_, index) => normalizedTeamSize + index),
      );
    }
  }, [
    form,
    initialized,
    registerAsTeam,
    teamMembersFieldArray,
    leadHeadline,
    leadName,
    leadSkills,
    leadStory,
    watchedTeamSize,
  ]);

  const onboardingMutation = useMutation({
    mutationFn: saveCandidateOnboarding,
    onSuccess: (_data, variables) => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      }
      queryClient.invalidateQueries({ queryKey: ['candidate-profile'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['candidate-profile-score'] });
      queryClient.setQueriesData(
        { queryKey: ['candidate-onboarding-status'], exact: false },
        { onboardingCompleted: true },
      );
      queryClient.invalidateQueries({ queryKey: ['candidate-onboarding-status'] });
      toast.success('Onboarding complete! AI matches will refresh shortly.', {
        description: 'You can update these settings anytime from your profile.',
      });
      posthog.capture('candidate_onboarding_complete', { totalSteps: steps.length });
      setShowCompletion(true);
      const destination = variables.registerAsTeam ? '/candidate/team-dashboard' : '/candidate/dashboard';
      setTimeout(() => navigate(destination), 1500);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to save onboarding details. Please try again.';
      toast.error(message);
      posthog.capture('candidate_onboarding_error', { message });
    },
  });

  const currentStepIndex = steps.findIndex((step) => step.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;
  const liveValues = useWatch({ control: form.control });
  const isLastStep = currentStepIndex === steps.length - 1;

  useEffect(() => {
    if (typeof window === 'undefined' || !initialized) return;
    const subscription = form.watch((value) => {
      try {
        window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        console.warn('Failed to persist onboarding draft', error);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, initialized]);

  useEffect(() => {
    if (!initialized) return;
    posthog.capture('candidate_onboarding_step_view', {
      step: currentStep,
      stepIndex: currentStepIndex + 1,
      progress,
    });
  }, [currentStep, currentStepIndex, initialized, progress]);

  const goNext = async () => {
    const fields = stepFieldMap[currentStep];
    const valid = await form.trigger(fields, { shouldFocus: true });
    if (!valid) {
      toast.error('Please complete the required fields to continue.');
      return;
    }
    if (currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1].id;
      posthog.capture('candidate_onboarding_step_advance', { from: currentStep, to: nextStep });
      setCurrentStep(nextStep);
    }
  };

  const goBack = () => {
    if (currentStepIndex === 0) return;
    const previousStep = steps[currentStepIndex - 1].id;
    posthog.capture('candidate_onboarding_step_back', { from: currentStep, to: previousStep });
    setCurrentStep(previousStep);
  };

  const onSubmit = async (values: OnboardingFormValues) => {
    posthog.capture('candidate_onboarding_submit', {
      step: currentStep,
      totalSteps: steps.length,
    });
    const payload: CandidateOnboardingPayload = {
      fullName: values.fullName,
      headline: values.headline,
      story: values.story,
      returner: values.returner,
      registerAsTeam: values.registrationMode === 'team',
      teamValueProposition: values.teamValueProposition,
      teamReliability: values.teamReliability,
      teamAchievements: values.teamAchievements,
      teamHistory: values.teamHistory,
      teamTenureYears: values.teamTenureYears,
      teamSize: values.teamSize,
      teamMembers: values.teamMembers,
      experienceLevel: values.experienceLevel,
      skills: values.skills,
      focusAreas: values.focusAreas,
      desiredRoles: values.desiredRoles,
      locationPreference: values.locationPreference,
      workStyle: values.workStyle,
      salaryRange: values.salaryRange,
      availabilityDate: values.availabilityDate,
      jobAlerts: values.jobAlerts,
      alertFrequency: values.alertFrequency,
      notificationChannels: values.notificationChannels,
    };
    await onboardingMutation.mutateAsync(payload);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'basics':
        return <BasicsStep form={form} teamMembersFieldArray={teamMembersFieldArray} />;
      case 'skills':
        return <SkillsStep form={form} />;
      case 'preferences':
        return <PreferencesStep form={form} />;
      case 'alerts':
        return <AlertsStep form={form} liveValues={liveValues} />;
      default:
        return null;
    }
  };

  if (showCompletion) {
    return (
      <main
        className="flex min-h-screen flex-col items-center justify-center bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)] px-4 text-center"
        aria-labelledby="candidate-onboarding-complete"
      >
        <div className="space-y-4 rounded-3xl border border-white/10 bg-black/30 p-10 shadow-xl shadow-black/40">
          <p className="text-xs uppercase tracking-[0.4em] text-blue-200/80">All set</p>
          <h1 id="candidate-onboarding-complete" className="text-3xl font-semibold text-gold-gradient">Onboarding complete</h1>
          <p className="text-gray-300 max-w-md">
            We’re refreshing your matches and sending you to the dashboard.
          </p>
        </div>
      </main>
    );
  }

  if (!initialized) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#030712] text-white" aria-label="Loading onboarding">
        <p>Loading your onboarding experience...</p>
      </main>
    );
  }

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="candidate-onboarding-title"
    >
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/candidate/dashboard" className="text-sm text-gray-400 hover:text-white">
            ← Back to dashboard
          </Link>
          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">Autosaving</Badge>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10 space-y-8">
        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span id="candidate-onboarding-title">Candidate onboarding</span>
              <span className="text-sm font-normal text-gray-400">
                Step {currentStepIndex + 1} of {steps.length}
              </span>
            </CardTitle>
            <Progress value={progress} className="h-2 bg-white/10" />
            <div className="mt-4 grid gap-4 sm:grid-cols-4">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    'rounded-xl border px-3 py-2 text-sm transition',
                    index === currentStepIndex
                      ? 'border-[#d2ac47]/60 bg-[#ba9036]/10 text-white'
                      : 'border-white/10 text-gray-400',
                  )}
                >
                  <p className="font-semibold">{step.title}</p>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </CardHeader>

          <CardContent className="p-6">
            <Form {...form}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!isLastStep) {
                    void goNext();
                  }
                }}
                className="space-y-8"
              >
                {renderStep()}

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={goBack}
                    disabled={currentStepIndex === 0}
                    className="text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  {isLastStep ? (
                    <Button
                      type="button"
                      className="bg-gold-gradient text-black hover:brightness-110"
                      onClick={() => void form.handleSubmit(onSubmit)()}
                      disabled={onboardingMutation.isPending}
                    >
                      {onboardingMutation.isPending ? 'Saving…' : 'Launch matches'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      className="bg-gold-gradient text-black hover:brightness-110"
                      onClick={goNext}
                      disabled={onboardingMutation.isPending}
                    >
                      Continue
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

type MultiValueField = 'skills' | 'focusAreas' | 'desiredRoles';
type TeamMembersFieldArray = ReturnType<typeof useFieldArray<OnboardingFormValues, 'teamMembers'>>;

type MultiValueInputProps = {
  form: UseFormReturn<OnboardingFormValues>;
  name: MultiValueField;
  label: string;
  placeholder: string;
  description?: string;
  suggestions?: string[];
  liveSuggestions?: string[];
  onInputChange?: (value: string) => void;
};

function MultiValueInput({
  form,
  name,
  label,
  placeholder,
  description,
  suggestions,
  liveSuggestions,
  onInputChange,
}: MultiValueInputProps) {
  const [inputValue, setInputValue] = useState('');

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const values = Array.isArray(field.value) ? field.value : [];

        const addValue = (value: string) => {
          const trimmed = value.trim();
          if (!trimmed || values.includes(trimmed)) return;
          const next = [...values, trimmed];
          field.onChange(next);
          setInputValue('');
        };

        const removeValue = (value: string) => {
          const next = values.filter((item) => item !== value);
          field.onChange(next);
        };

        return (
          <FormItem>
            <FormLabel className="text-white">{label}</FormLabel>
            <FormControl>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Input
                    value={inputValue}
                    onChange={(event) => {
                      setInputValue(event.target.value);
                      onInputChange?.(event.target.value);
                    }}
                    placeholder={placeholder}
                    className="flex-1 border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addValue(inputValue);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                    onClick={() => addValue(inputValue)}
                  >
                    Add
                  </Button>
                </div>
                {description && <FormDescription className="text-gray-400">{description}</FormDescription>}
                {!!values.length && (
                  <div className="flex flex-wrap gap-2">
                    {values.map((value) => (
                      <span
                        key={value}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm text-gray-100"
                      >
                        {value}
                        <button
                          type="button"
                          aria-label={`Remove ${value}`}
                          onClick={() => removeValue(value)}
                          className="text-xs text-gray-400 hover:text-white"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {(suggestions?.length || liveSuggestions?.length) ? (
                  <div className="flex flex-wrap gap-2 text-xs">
                    {Array.from(new Set([...(suggestions ?? []), ...(liveSuggestions ?? [])])).map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => addValue(suggestion)}
                        className="rounded-full border border-[#d2ac47]/30 bg-[#ba9036]/10 px-3 py-1 text-[#f7ef8a] transition hover:bg-[#ba9036]/20"
                      >
                        + {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

function BasicsStep({
  form,
  teamMembersFieldArray,
}: {
  form: UseFormReturn<OnboardingFormValues>;
  teamMembersFieldArray: TeamMembersFieldArray;
}) {
  const registrationMode = form.watch('registrationMode');
  const teamRegistrationEnabled = registrationMode === 'team';

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <FormField
        control={form.control}
        name="fullName"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">
              {teamRegistrationEnabled ? 'Team lead / contact name' : 'Full name'}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={teamRegistrationEnabled ? 'e.g., Maya Patel, Team Lead' : 'e.g., Maya Patel'}
                className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="experienceLevel"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Experience level</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-white/15 bg-white/10 text-white">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                <SelectItem value="early">Early career (0-3 yrs)</SelectItem>
                <SelectItem value="mid">Mid level (4-8 yrs)</SelectItem>
                <SelectItem value="senior">Senior / Lead</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="headline"
        render={({ field }) => (
          <FormItem className="lg:col-span-2">
            <FormLabel className="text-white">
              {teamRegistrationEnabled ? 'Team lead headline' : 'Professional headline'}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={
                  teamRegistrationEnabled
                    ? 'e.g., Clinical Operations Lead | Complex Care & Community Coverage'
                    : 'e.g., Programme manager re-entering fintech after parental leave'
                }
                className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="story"
        render={({ field }) => (
          <FormItem className="lg:col-span-2">
            <FormLabel className="text-white">
              {teamRegistrationEnabled ? 'Team lead summary' : 'Your story'}
            </FormLabel>
            <FormControl>
              <Textarea
                {...field}
                rows={6}
                placeholder={
                  teamRegistrationEnabled
                    ? 'Summarise leadership scope, sector experience, and delivery standards your team follows.'
                    : "Share your experience, key achievements, and what you're excited to do next."
                }
                className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="returner"
        render={({ field }) => (
          <FormItem className="lg:col-span-2 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
            <div>
              <FormLabel className="text-white">I&apos;m returning to work after a break</FormLabel>
              <FormDescription className="text-gray-400">Helps employers understand your journey.</FormDescription>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="registrationMode"
        render={({ field }) => (
          <FormItem className="lg:col-span-2">
            <FormLabel className="text-white">How do you want to register?</FormLabel>
            <FormDescription className="text-gray-400">
              Choose one option: solo candidate or team candidate.
            </FormDescription>
            <FormControl>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => field.onChange('solo')}
                  className={cn(
                    'rounded-xl border px-4 py-3 text-left transition',
                    field.value === 'solo'
                      ? 'border-[#d2ac47]/70 bg-[#ba9036]/15 text-white'
                      : 'border-white/10 bg-white/[0.03] text-gray-300 hover:border-[#d2ac47]/40',
                  )}
                >
                  <p className="font-semibold">Solo</p>
                  <p className="text-sm text-gray-400">I am registering as one candidate profile.</p>
                </button>
                <button
                  type="button"
                  onClick={() => field.onChange('team')}
                  className={cn(
                    'rounded-xl border px-4 py-3 text-left transition',
                    field.value === 'team'
                      ? 'border-[#d2ac47]/70 bg-[#ba9036]/15 text-white'
                      : 'border-white/10 bg-white/[0.03] text-gray-300 hover:border-[#d2ac47]/40',
                  )}
                >
                  <p className="font-semibold">Team</p>
                  <p className="text-sm text-gray-400">I am onboarding multiple people under one account.</p>
                </button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {teamRegistrationEnabled ? (
        <>
          <Card className="lg:col-span-2 border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="text-base text-white">Team profile</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-2">
              <FormField
                control={form.control}
                name="teamTenureYears"
                render={({ field }) => (
                  <FormItem className="lg:col-span-2">
                    <FormLabel className="text-white">How long has this team been together? (years)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={80}
                        placeholder="e.g., 4"
                        value={field.value}
                        onChange={(event) => field.onChange(Number(event.target.value))}
                        className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormDescription className="text-gray-400">
                      Add the total time your current team has worked together.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamValueProposition"
                render={({ field }) => (
                  <FormItem className="lg:col-span-2">
                    <FormLabel className="text-white">What does your team offer?</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Describe your core service offering, specialist strengths, and the outcomes clients can expect."
                        className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamReliability"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Why is your team reliable?</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Explain your coverage model, compliance standards, escalation process, and continuity plans."
                        className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamAchievements"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Team achievements and progress</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Include measurable outcomes: response times, satisfaction scores, retention, audits, or awards."
                        className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamHistory"
                render={({ field }) => (
                  <FormItem className="lg:col-span-2">
                    <FormLabel className="text-white">Team background and journey</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Tell the team's story: when it formed, how it evolved, and the type of assignments completed."
                        className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <FormField
            control={form.control}
            name="teamSize"
            render={({ field }) => (
              <FormItem className="lg:col-span-2">
                <FormLabel className="text-white">Number of people in the team</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={2}
                    max={50}
                    value={field.value}
                    onChange={(event) => field.onChange(Number(event.target.value))}
                    className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                  />
                </FormControl>
                <FormDescription className="text-gray-400">
                  Team member 1 is the team lead from above. Add the rest of the team below.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="lg:col-span-2 space-y-4">
            {teamMembersFieldArray.fields.slice(1).map((member, index) => {
              const memberIndex = index + 1;
              return (
              <Card key={member.id} className="border-white/10 bg-white/[0.03]">
                <CardHeader>
                  <CardTitle className="text-base text-white">Team member {memberIndex + 1}</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name={`teamMembers.${memberIndex}.fullName` as const}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., Maya Patel"
                            className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`teamMembers.${memberIndex}.headline` as const}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Role headline</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., Senior Support Worker (Medication Certified)"
                            className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`teamMembers.${memberIndex}.cvSummary` as const}
                    render={({ field }) => (
                      <FormItem className="lg:col-span-2">
                        <FormLabel className="text-white">CV summary</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={4}
                            placeholder="Highlight role focus, years of experience, and key responsibilities delivered."
                            className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`teamMembers.${memberIndex}.cvSkills` as const}
                    render={({ field }) => (
                      <FormItem className="lg:col-span-2">
                        <FormLabel className="text-white">CV skills (comma separated)</FormLabel>
                        <FormControl>
                          <Input
                            value={(field.value ?? []).join(', ')}
                            onChange={(event) =>
                              field.onChange(
                                event.target.value
                                  .split(',')
                                  .map((skill) => skill.trim())
                                  .filter(Boolean),
                              )
                            }
                            placeholder="e.g., Patient care, Manual handling, Communication"
                            className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormDescription className="text-gray-400">
                          These skills are used for team-level candidate matching.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

function SkillsStep({ form }: { form: UseFormReturn<OnboardingFormValues> }) {
  const [skillsQuery, setSkillsQuery] = useState('');
  const [rolesQuery, setRolesQuery] = useState('');
  const skillsSearch = useSkillsSearch(skillsQuery);
  const rolesSearch = useOccupationSearch(rolesQuery);

  const skillSuggestionsLive = useMemo(
    () => (skillsSearch.data ?? []).map((skill) => skill.preferredLabel),
    [skillsSearch.data],
  );
  const roleSuggestionsLive = useMemo(
    () => (rolesSearch.data ?? []).map((occ) => occ.preferredLabel),
    [rolesSearch.data],
  );

  return (
    <div className="space-y-6">
      <MultiValueInput
        form={form}
        name="skills"
        label="Core skills"
        placeholder="Add a skill and press enter"
        description="Add the skills you want Lighthouse to spotlight."
        suggestions={skillSuggestions}
        liveSuggestions={skillSuggestionsLive}
        onInputChange={setSkillsQuery}
      />
      <MultiValueInput
        form={form}
        name="focusAreas"
        label="Focus areas"
        placeholder="Add a focus area"
        description="Choose industries or missions that excite you."
        suggestions={focusAreaSuggestions}
      />
      <MultiValueInput
        form={form}
        name="desiredRoles"
        label="Target roles"
        placeholder="Add a target role"
        description="Helps us tailor job matches and alerts."
        suggestions={roleSuggestions}
        liveSuggestions={roleSuggestionsLive}
        onInputChange={setRolesQuery}
      />
    </div>
  );
}

function PreferencesStep({ form }: { form: UseFormReturn<OnboardingFormValues> }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <FormField
        control={form.control}
        name="locationPreference"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Location or remote preference</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="e.g., London hybrid, Remote within UK"
                className="border-white/15 bg-white/10 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="workStyle"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Work style</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-white/15 bg-white/10 text-white">
                  <SelectValue placeholder="Select work style" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                <SelectItem value="remote">Remote first</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
                <SelectItem value="onsite">On-site</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="salaryRange"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Salary expectation</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., £55k – £65k" className="border-white/15 bg-white/10 text-white placeholder:text-gray-400" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="availabilityDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white">Earliest start date</FormLabel>
            <FormControl>
              <Input
                type="date"
                {...field}
                className="border-white/15 bg-white/10 text-white placeholder:text-gray-400 [color-scheme:dark]"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function AlertsStep({
  form,
  liveValues,
}: {
  form: UseFormReturn<OnboardingFormValues>;
  liveValues: DeepPartial<OnboardingFormValues>;
}) {
  const teamMemberNames = (liveValues.teamMembers ?? [])
    .map((member) => member?.fullName?.trim())
    .filter((name): name is string => Boolean(name));

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="jobAlerts"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div>
                <FormLabel className="text-white">Job alerts</FormLabel>
                <FormDescription className="text-gray-400">Stay updated with curated roles.</FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="grid gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="alertFrequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Frequency</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!form.getValues('jobAlerts')}>
                  <FormControl>
                    <SelectTrigger className="border-white/15 bg-white/10 text-white">
                      <SelectValue placeholder="Select cadence" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                    <SelectItem value="daily">Daily digests</SelectItem>
                    <SelectItem value="weekly">Weekly summary</SelectItem>
                    <SelectItem value="off">Pause alerts</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notificationChannels"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Channels</FormLabel>
                <div className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-4">
                  {['email', 'sms', 'in-app'].map((channel) => (
                    <label key={channel} className="flex items-center gap-3 text-sm text-white capitalize">
                      <Checkbox
                        checked={field.value?.includes(channel as OnboardingFormValues['notificationChannels'][number])}
                        onCheckedChange={(checked) => {
                          const next = checked
                            ? [...field.value, channel]
                            : field.value.filter((item) => item !== channel);
                          field.onChange(next);
                        }}
                      />
                      {channel === 'sms' ? 'SMS' : channel === 'in-app' ? 'In-app' : 'Email'}
                    </label>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <Card className="border-white/10 bg-white/[0.02]">
        <CardHeader>
          <CardTitle className="text-white">Summary</CardTitle>
          <p className="text-sm text-gray-400">Review the details you&apos;ve provided.</p>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-200">
          <SummaryRow label="Headline" value={liveValues.headline || 'Not set'} />
          <SummaryRow label="Experience level" value={labelForExperience(liveValues.experienceLevel)} />
          <SummaryRow label="Registration type" value={liveValues.registrationMode === 'team' ? 'Team' : liveValues.registrationMode === 'solo' ? 'Solo' : 'Not set'} />
          {liveValues.registrationMode === 'team' ? (
            <>
              <SummaryRow label="Team tenure" value={liveValues.teamTenureYears !== undefined ? `${liveValues.teamTenureYears} years` : 'Not set'} />
              <SummaryRow label="Team value proposition" value={liveValues.teamValueProposition || 'Not set'} />
              <SummaryRow label="Team reliability" value={liveValues.teamReliability || 'Not set'} />
              <SummaryRow label="Team achievements" value={liveValues.teamAchievements || 'Not set'} />
              <SummaryRow label="Team history" value={liveValues.teamHistory || 'Not set'} />
              <SummaryRow label="Team size" value={liveValues.teamSize ? String(liveValues.teamSize) : 'Not set'} />
              <SummaryRow
                label="Team members"
                value={formatList(teamMemberNames)}
              />
            </>
          ) : null}
          <SummaryRow label="Skills" value={formatList(liveValues.skills)} />
          <SummaryRow label="Focus areas" value={formatList(liveValues.focusAreas)} />
          <SummaryRow label="Target roles" value={formatList(liveValues.desiredRoles)} />
          <SummaryRow label="Location preference" value={liveValues.locationPreference || 'Not set'} />
          <SummaryRow label="Work style" value={labelForWorkStyle(liveValues.workStyle)} />
          <SummaryRow label="Salary expectation" value={liveValues.salaryRange || 'Not set'} />
          <SummaryRow label="Job alerts" value={liveValues.jobAlerts ? 'Enabled' : 'Disabled'} />
          {liveValues.jobAlerts && (
            <>
              <SummaryRow label="Alert frequency" value={liveValues.alertFrequency === 'daily' ? 'Daily' : liveValues.alertFrequency === 'weekly' ? 'Weekly' : 'Paused'} />
              <SummaryRow label="Channels" value={formatList(liveValues.notificationChannels)} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
      <span className="text-gray-400">{label}</span>
      <span className="text-right text-white">{value || 'Not provided'}</span>
    </div>
  );
}

function formatList(value?: Array<string | undefined>) {
  if (!value || value.length === 0) return 'Not set';
  const filtered = value.map((item) => item?.trim()).filter((item): item is string => Boolean(item));
  if (!filtered.length) return 'Not set';
  return filtered.join(', ');
}

function labelForExperience(value?: OnboardingFormValues['experienceLevel']) {
  switch (value) {
    case 'early':
      return 'Early career';
    case 'mid':
      return 'Mid level';
    case 'senior':
      return 'Senior / Lead';
    default:
      return 'Not set';
  }
}

function labelForWorkStyle(value?: OnboardingFormValues['workStyle']) {
  switch (value) {
    case 'remote':
      return 'Remote first';
    case 'hybrid':
      return 'Hybrid';
    case 'onsite':
      return 'On-site';
    default:
      return 'Not set';
  }
}
